/**
 * Pure helpers for the deals route (detail-view tabs and listing updates).
 * Keeps route component thin and logic testable.
 */

import type { DetailViewDeal } from '../../../../pbi/components/deals/deals.types';

export interface ApplyListingUpdatesResult<T extends DetailViewDeal> {
  updated: T[];
  changed: boolean;
}

/**
 * Compares listing deal fields against current deal to detect changes.
 */
function hasFieldChanges(current: DetailViewDeal, listing: DetailViewDeal): boolean {
  return Object.keys(listing).some(
    (key) =>
      (listing as Record<string, unknown>)[key] !==
      (current as Record<string, unknown>)[key],
  );
}

/**
 * Merges listing deal data into detailViewDeals by DEAL_ID. For each deal in listingDeals,
 * if there is a matching detail view deal, replaces it with a merge of existing + listing fields
 * so all fields from the dataserver update are reflected in the tab.
 */
export function applyListingUpdatesToDetailView<T extends DetailViewDeal>(
  detailViewDeals: T[],
  listingDeals: DetailViewDeal[],
): ApplyListingUpdatesResult<T> {
  const byId = new Map<string, DetailViewDeal>();
  for (const d of listingDeals) {
    byId.set(String(d.DEAL_ID), d);
  }

  let changed = false;
  const updated = detailViewDeals.map((d) => {
    const fromListing = byId.get(String(d.DEAL_ID));
    if (!fromListing) {
      return d;
    }
    if (!hasFieldChanges(d, fromListing)) {
      return d;
    }
    changed = true;
    return { ...d, ...fromListing } as T;
  });

  return { updated, changed };
}
